import { Injectable, OnDestroy } from '@angular/core'
import Keycloak, { KeycloakInstance } from 'keycloak-js'
import { environment } from '../../../environments/environment'

interface KeycloakTokenParsed {
  exp?: number
  iat?: number
  jti?: string
  iss?: string
  aud?: string | string[]
  sub?: string
  email?: string
  email_verified?: boolean
  name?: string
  preferred_username?: string
  given_name?: string
  family_name?: string
  countries?: string[]
  realm_access?: {
    roles: string[]
  }
  resource_access?: {
    [clientId: string]: {
      roles: string[]
    }
  }
  scope?: string
}

@Injectable({
  providedIn: 'root'
})
export class KeycloakService implements OnDestroy {
  private keycloak: KeycloakInstance
  private initialized = false
  private tokenRefreshInterval: ReturnType<typeof setInterval> | null = null
  private readonly TOKEN_REFRESH_INTERVAL_MS = environment.keycloakRefreshLoginMs

  async init(): Promise<boolean> {
    this.keycloak = new Keycloak({
      url: environment.keycloakUrl,
      realm: environment.keycloakRealm,
      clientId: environment.keycloakClientId
    })

      this.setupTokenExpirationCallback()

      const keycloakInit = await this.keycloak.init({
        onLoad: 'check-sso',
        checkLoginIframe: false,
        silentCheckSsoRedirectUri: `${window.location.origin}/assets/silent-check-sso.html`,
      } as any) as any
      this.initialized = true

      if (keycloakInit) {
        this.startTokenRefreshHeartbeat()
      }

      return keycloakInit as boolean
  }

  login(redirectUri?: string): void {
    if (!this.initialized) {
      console.error('Keycloak is not initialized')
      return
    }
    const options = redirectUri ? { redirectUri } : {}
    this.keycloak.login(options)
  }

  logout(): void {
    if (!this.initialized) {
      console.error('Keycloak is not initialized')
      return
    }
    // Stop heartbeat before logout
    this.stopTokenRefreshHeartbeat()
    this.keycloak.logout({
      redirectUri: `${window.location.origin}/login`
    })
  }

  isAuthenticated(): boolean {
    return this.initialized && this.keycloak.authenticated
  }

  getToken(): string | undefined {
    return this.keycloak?.token
  }

  getRefreshToken(): string | undefined {
    return this.keycloak?.refreshToken
  }

  async updateToken(minValidity = 30): Promise<boolean> {
    if (!this.initialized) {
      return false
    }

    return await this.keycloak.updateToken(minValidity) as boolean
  }

  getTokenParsed(): KeycloakTokenParsed | undefined {
    return this.keycloak?.tokenParsed as any as KeycloakTokenParsed
  }

  getUsername(): string | undefined {
    const token = this.getTokenParsed()
    return token?.preferred_username
  }

  getUserEmail(): string | undefined {
    const token = this.getTokenParsed()
    return token?.email
  }

  getUserCountries(): string[] | undefined {
    const token = this.getTokenParsed()
    return token?.countries
  }

  getClientRoles(): string[] {
    const token = this.getTokenParsed()
    if (!token?.resource_access) return []

    const clientRoles = token.resource_access[environment.keycloakClientId]?.roles
    return clientRoles || []
  }

  hasRealmRole(role: string): boolean {
    return this.keycloak?.hasRealmRole(role) || false
  }

  hasResourceRole(role: string, resource?: string): boolean {
    return this.keycloak?.hasResourceRole(role, resource) || false
  }

  private startTokenRefreshHeartbeat(): void {
    this.stopTokenRefreshHeartbeat()

    if (!this.isAuthenticated()) {
      return
    }

    this.updateToken(30).catch(error => {
      console.error('Failed to refresh token on heartbeat start:', error)
    })

    this.tokenRefreshInterval = setInterval(async () => {
      if (!this.isAuthenticated()) {
        this.stopTokenRefreshHeartbeat()
        return
      }

      try {
        await this.updateToken(30)
        console.debug('Token refreshed by heartbeat')
      } catch (error) {
        console.error('Failed to refresh token during heartbeat:', error)
        this.stopTokenRefreshHeartbeat()
      }
    }, this.TOKEN_REFRESH_INTERVAL_MS)
  }

  private stopTokenRefreshHeartbeat(): void {
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval)
      this.tokenRefreshInterval = null
    }
  }

  private setupTokenExpirationCallback(): void {
    this.keycloak.onTokenExpired = () => {
      console.warn('Token expired, attempting to refresh as fallback')
      this.updateToken(30).catch(error => {
        console.error('Failed to refresh expired token, logging out:', error)
        this.logout()
      })
    }
  }

  ngOnDestroy(): void {
    this.stopTokenRefreshHeartbeat()
  }
}
